// chat.js — agentic chat for Pronto. Streaming, tool use, prompt caching.
//
// Architecture
// - Single endpoint (POST /v1/messages, stream=true). We handle the agentic
//   loop ourselves because the SDK's tool runner isn't browser-friendly here.
// - Read tools auto-execute. Write tools route through requestApproval(),
//   which the UI presents as inline confirmation cards.
// - System prompt is split: a stable "frozen" block (instructions + tool
//   guide) marked cache_control: ephemeral, then a small dynamic context
//   summary appended after. Caching is prefix-match — keep the frozen part
//   first and verbatim.
// - History persists in localStorage per Clerk userId, capped to a sane
//   size so a long conversation doesn't blow out browser storage.

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabase";

const API_URL = "https://api.anthropic.com/v1/messages";
const MODEL = "claude-sonnet-4-6";

// Persistence — keep ~30 turns max. Each turn ≈ user msg + assistant msg
// (+ optional tool round-trips), so this gives a rich back-context without
// risking localStorage quota issues.
const HISTORY_LS_PREFIX = "cucina-chat:";
const MAX_PERSISTED_MESSAGES = 60;

function emitUsage(kind, usage) {
  if (!usage || typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("cucina-ai-usage", {
      detail: { kind, model: MODEL, usage },
    }));
  } catch {}
}

// ---------------------------------------------------------------------------
// Tool definitions
// ---------------------------------------------------------------------------

export const TOOL_DEFS = [
  {
    name: "search_pantry",
    description: "Search the user's pantry inventory. Returns matching items with quantity, brand, and category. Use when the user asks what they have, what's expiring, or whether they have a specific ingredient. Omit query to list everything.",
    input_schema: {
      type: "object",
      properties: {
        query: { type: "string", description: "Optional substring to filter on (matches item name and brand). Omit to return all items." },
        category: { type: "string", description: "Optional category filter: pantry, fridge, freezer, produce, etc." },
      },
    },
  },
  {
    name: "search_recipes",
    description: "Search the user's recipe archive by query, mood, or time budget. Returns titles, mood tags, and total time. Use get_recipe afterwards to read ingredients/steps.",
    input_schema: {
      type: "object",
      properties: {
        query: { type: "string", description: "Optional substring to match title/cuisine/ingredient name." },
        mood: { type: "string", description: "Optional mood tag (cozy, quick, hearty, light, etc.)." },
        max_minutes: { type: "number", description: "Optional max total cook time." },
        favorites_only: { type: "boolean", description: "If true, only return favorited recipes." },
      },
    },
  },
  {
    name: "get_recipe",
    description: "Get the full recipe — ingredients with amounts, prep steps, appliances needed, source. Use after search_recipes finds something interesting.",
    input_schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "Recipe ID (rcp_...)." },
      },
      required: ["id"],
    },
  },
  {
    name: "list_appliances",
    description: "List the user's owned appliances with categories.",
    input_schema: { type: "object", properties: {} },
  },
  {
    name: "list_beans",
    description: "List the user's current coffee beans — roaster, roast level, days off roast, tasting notes. Use for any coffee/brew question.",
    input_schema: { type: "object", properties: {} },
  },
  {
    name: "get_taste_profile",
    description: "Get the user's calibrated coffee taste profile — grind/temp/ratio biases learned from their brews. Use before suggesting brew changes.",
    input_schema: { type: "object", properties: {} },
  },
  {
    name: "get_recent_brews",
    description: "Get recent brew history with feedback and ratings.",
    input_schema: {
      type: "object",
      properties: {
        limit: { type: "number", description: "How many brews to return. Default 5." },
      },
    },
  },
  {
    name: "get_shopping_list",
    description: "Get the current shopping list with done/pending status.",
    input_schema: { type: "object", properties: {} },
  },
  // ---- write tools (require user approval) ----
  {
    name: "add_shopping_item",
    description: "Add an item to the user's shopping list. Call this directly when the user asks to add something — the app shows a confirmation card automatically.",
    input_schema: {
      type: "object",
      properties: {
        text: { type: "string", description: "What to add (e.g. 'milk', '2 lemons', 'fresh basil')." },
      },
      required: ["text"],
    },
  },
  {
    name: "mark_recipe_made",
    description: "Log that the user just cooked a recipe. Increments their times-made counter. Call directly — the app confirms with the user.",
    input_schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "Recipe ID (rcp_...)." },
        servings_made: { type: "number", description: "Optional servings actually made." },
      },
      required: ["id"],
    },
  },
  {
    name: "toggle_favorite_recipe",
    description: "Toggle a recipe's favorite status. Call directly — the app confirms with the user.",
    input_schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "Recipe ID (rcp_...)." },
      },
      required: ["id"],
    },
  },
  {
    name: "save_recipe",
    description: "Save a NEW recipe you just created in the conversation to Josh's recipe archive. Call this whenever you write out a full, complete recipe (title + ingredients + steps) that isn't already in his archive — the app shows Josh a keep/discard card with the suggested name so he decides whether to keep it. Do NOT call this for recipes that came from search_recipes/get_recipe (those already exist). Do NOT ask permission in chat first — just call the tool.",
    input_schema: {
      type: "object",
      properties: {
        title: { type: "string", description: "A concise, appetizing recipe name (e.g. 'Weeknight Lemon Garlic Chicken')." },
        summary: { type: "string", description: "One or two sentences describing the dish." },
        cuisine: { type: "array", items: { type: "string" }, description: "Cuisine tags, lowercase (e.g. ['italian'])." },
        course: { type: "array", items: { type: "string" }, description: "Course tags, lowercase (e.g. ['dinner', 'main'])." },
        mood_tags: { type: "array", items: { type: "string" }, description: "Mood tags, lowercase (cozy, quick, hearty, light, etc.)." },
        diet_tags: { type: "array", items: { type: "string" }, description: "Diet tags, lowercase (vegetarian, gluten-free, etc.)." },
        yield_amount: { type: "number", description: "How many servings this makes." },
        yield_unit: { type: "string", description: "Yield unit, usually 'servings'." },
        prep_min: { type: "number", description: "Prep time in minutes." },
        active_min: { type: "number", description: "Active cook time in minutes." },
        total_min: { type: "number", description: "Total time in minutes." },
        ingredients: {
          type: "array",
          description: "Ingredient list.",
          items: {
            type: "object",
            properties: {
              name: { type: "string", description: "Ingredient name (e.g. 'garlic')." },
              quantity: { type: "string", description: "Amount as written (e.g. '2', '1/2', 'a pinch')." },
              unit: { type: "string", description: "Unit (e.g. 'cloves', 'cup', 'tbsp'). Omit if none." },
              preparation: { type: "string", description: "Prep note (e.g. 'minced', 'room temperature'). Omit if none." },
              section: { type: "string", description: "Optional subsection heading (e.g. 'For the sauce')." },
            },
            required: ["name"],
          },
        },
        steps: {
          type: "array",
          description: "Ordered preparation steps, each a full instruction sentence.",
          items: { type: "string" },
        },
        notes: { type: "string", description: "Optional tips or variations." },
      },
      required: ["title", "ingredients", "steps"],
    },
  },
];

export const WRITE_TOOL_NAMES = new Set([
  "add_shopping_item",
  "mark_recipe_made",
  "toggle_favorite_recipe",
  "save_recipe",
]);

// ---------------------------------------------------------------------------
// Tool execution
// ---------------------------------------------------------------------------

function fmt(obj) { return JSON.stringify(obj, null, 2); }

// Concise inventory summary for results — drops redundant Supabase metadata
function inventorySummary(entry) {
  const sku = entry.sku || {};
  return {
    id: entry.id,
    name: sku.canonical_name || sku.name || "(unnamed)",
    brand: sku.brand || "",
    category: sku.category || "",
    quantity: entry.quantity ?? 1,
    unit: entry.unit || sku.unit || "",
    expires_on: entry.expires_at || null,
  };
}

function recipeSummary(r) {
  return {
    id: r.id,
    title: r.title,
    cuisine: r.cuisine || [],
    mood_tags: r.mood_tags || [],
    total_min: r.total_min || r.meta?.time?.total_min || 0,
    diet_tags: r.diet_tags || [],
    favorite: !!r.favorite,
    times_made: r.times_made || 0,
  };
}

function beanSummary(b) {
  const days = b.roast_date
    ? Math.floor((Date.now() - new Date(b.roast_date).getTime()) / 86400000)
    : null;
  return {
    id: b.id,
    name: b.name,
    roaster: b.roaster || "",
    roast_level: b.roast_level || "",
    process: b.process || "",
    origin: b.origin || [],
    tasting_notes: b.tasting_notes || [],
    days_off_roast: days,
  };
}

export async function executeTool(name, input, store) {
  switch (name) {
    case "search_pantry": {
      const q = (input.query || "").toLowerCase().trim();
      const cat = (input.category || "").toLowerCase().trim();
      const matches = (store.inventory || []).filter(e => {
        const sku = e.sku || {};
        const hay = `${sku.canonical_name || ""} ${sku.name || ""} ${sku.brand || ""}`.toLowerCase();
        if (q && !hay.includes(q)) return false;
        if (cat && (sku.category || "").toLowerCase() !== cat) return false;
        return true;
      });
      return fmt({
        total: matches.length,
        items: matches.slice(0, 40).map(inventorySummary),
        truncated: matches.length > 40,
      });
    }

    case "search_recipes": {
      const q = (input.query || "").toLowerCase().trim();
      const mood = (input.mood || "").toLowerCase().trim();
      const max = input.max_minutes;
      const favOnly = !!input.favorites_only;
      const matches = (store.recipes || []).filter(r => {
        if (favOnly && !r.favorite) return false;
        if (q) {
          const hay = `${r.title} ${(r.cuisine || []).join(" ")} ${(r.ingredients || []).map(i => i.name).join(" ")}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (mood && !(r.mood_tags || []).map(t => t.toLowerCase()).includes(mood)) return false;
        if (max != null && (r.total_min || 999) > max) return false;
        return true;
      });
      return fmt({
        total: matches.length,
        recipes: matches.slice(0, 25).map(recipeSummary),
        truncated: matches.length > 25,
      });
    }

    case "get_recipe": {
      const r = (store.recipes || []).find(x => x.id === input.id);
      if (!r) return fmt({ error: `No recipe with id ${input.id}` });
      return fmt({
        id: r.id,
        title: r.title,
        summary: r.summary || r.meta?.summary || "",
        cuisine: r.cuisine || [],
        mood_tags: r.mood_tags || [],
        diet_tags: r.diet_tags || [],
        yield: { amount: r.yield_amount || 0, unit: r.yield_unit || "servings" },
        time: { total_min: r.total_min || 0, prep_min: r.prep_min || 0, active_min: r.active_min || 0 },
        ingredients: r.ingredients || [],
        steps: r.steps || [],
        appliances_needed: r.appliances_needed || [],
        source_url: r.source_url || "",
        notes: r.notes || "",
        times_made: r.times_made || 0,
        favorite: !!r.favorite,
      });
    }

    case "list_appliances": {
      const list = (store.appliances || []).map(a => ({
        id: a.id,
        name: a.name,
        category: a.category || "",
        brand: a.brand || "",
        model: a.model || "",
      }));
      return fmt({ total: list.length, appliances: list });
    }

    case "list_beans": {
      return fmt({
        total: (store.beans || []).length,
        beans: (store.beans || []).map(beanSummary),
      });
    }

    case "get_taste_profile": {
      const tp = store.tasteProfile || {};
      if (!tp.sample_count) return fmt({ calibrated: false, message: "No taste history yet — log some brews with ratings first." });
      return fmt({
        calibrated: true,
        sample_count: tp.sample_count,
        grind_bias: tp.grind_bias,
        temp_bias_c: tp.temp_bias_c,
        ratio_bias: tp.ratio_bias,
        bloom_time_bias_s: tp.bloom_time_bias_s,
        preferred_strength: tp.preferred_strength,
        preferred_clarity: tp.preferred_clarity,
      });
    }

    case "get_recent_brews": {
      const limit = Math.min(input.limit || 5, 20);
      const brews = (store.brewLogs || []).slice(0, limit).map(b => ({
        id: b.id,
        bean_name: (store.beans || []).find(x => x.id === b.bean_id)?.name || b.bean_id,
        brewed_at: b.brewed_at,
        rating: b.rating,
        feedback: b.feedback || "",
        dose_g: b.dose_g,
        water_ml: b.water_ml,
        ratio: b.dose_g && b.water_ml ? +(b.water_ml / b.dose_g).toFixed(1) : null,
        mode: b.mode || "hot",
      }));
      return fmt({ total: brews.length, brews });
    }

    case "get_shopping_list": {
      const items = (store.shoppingList || []).map(i => ({
        id: i.id,
        text: i.text,
        done: !!i.done,
        added_at: i.created_at,
      }));
      return fmt({ total: items.length, pending: items.filter(i => !i.done).length, items });
    }

    case "add_shopping_item": {
      const item = await store.addShoppingItem(input.text);
      return fmt({ ok: true, added: { id: item.id, text: item.text } });
    }

    case "mark_recipe_made": {
      const event = await store.recordCookEvent({
        recipeId: input.id,
        servingsMade: input.servings_made,
        rating: null,
        notes: "",
        adjustments: {},
      });
      return fmt({ ok: true, event_id: event.id });
    }

    case "toggle_favorite_recipe": {
      await store.toggleFavorite(input.id);
      const r = (store.recipes || []).find(x => x.id === input.id);
      return fmt({ ok: true, favorite: !!(r && r.favorite) });
    }

    case "save_recipe": {
      // The approval card may have overridden the title (Josh can edit the
      // suggested name before keeping). flattenRecipe (in useStore) handles the
      // flat shape — normalizing ingredients/steps — so pass the input through.
      const created = await store.addRecipe({
        title: input.title,
        summary: input.summary || "",
        source_type: "chat",
        cuisine: input.cuisine || [],
        course: input.course || [],
        mood_tags: input.mood_tags || [],
        diet_tags: input.diet_tags || [],
        yield_amount: input.yield_amount ?? 4,
        yield_unit: input.yield_unit || "servings",
        prep_min: input.prep_min || 0,
        active_min: input.active_min || 0,
        total_min: input.total_min || 0,
        ingredients: input.ingredients || [],
        steps: input.steps || [],
        notes: input.notes || "",
      });
      return fmt({ ok: true, id: created.id, title: created.title });
    }

    default:
      return fmt({ error: `Unknown tool: ${name}` });
  }
}

// ---------------------------------------------------------------------------
// System prompt (frozen + dynamic)
// ---------------------------------------------------------------------------
//
// FROZEN portion is stable across requests and gets cache_control. Keep it
// byte-identical between turns or caching breaks.

const FROZEN_SYSTEM = `You are Pronto, a helpful kitchen assistant inside Josh's personal cooking app. You can answer questions about his pantry, recipe archive, appliances, and coffee setup, and you can suggest what to cook or brew.

Style:
- Concise, conversational. No headers or bullet bloat for short answers.
- When you cite a recipe, always reference it by title (the user can tap to open).
- Be honest when you don't have the info — don't invent inventory or recipes.

Tools:
- For ANY question about pantry, recipes, appliances, beans, brews, or shopping — call the relevant lookup tool first. Don't guess from memory.
- When the user asks you to take an action (add something to shopping, log a recipe as cooked, favorite a recipe), CALL THE WRITE TOOL DIRECTLY. Do NOT ask permission in chat first — the app shows Josh a confirmation card automatically when you call the tool. Asking in chat AND calling the tool is wrong; just call the tool.
- If a write tool returns "User declined", acknowledge briefly and ask what they'd like instead. Don't retry the same write.
- Prefer search_recipes + get_recipe over re-asking the user for things you can look up.

Coffee — follow Josh's coffee handoff (docs/coffee-handoff.md is the full source of truth):
- Hardware: Fellow Aiden brewer + Fellow Ode Gen 2 grinder (Gen 2 brew burrs), filtered water, usually 8-cup batches.
- Always call get_taste_profile and get_recent_brews before suggesting changes — his calibrated biases override generic advice.
- Priorities, in order: easy on stomach, sweet, milk chocolate, caramel, smooth body, rich flavor (more flavor, not stronger), low bitterness, very low acidity, clean finish, a consistent repeatable daily recipe. He dislikes bitter, smoky, burnt, or ashy flavors, over-extraction, sharp acidity, and heavy body that sits in the stomach; French roasts score poorly.
- Bias EVERY recommendation toward smoother, sweeter, lower extraction, easier on the stomach — never toward stronger or more concentrated. Stomach comfort is the #1 priority. Coffee weight is the LAST variable to touch — adjust in this order: (1) grinder, (2) temp, (3) bloom, (4) pulse timing, (5) then dose. The Ode Gen 2 responds to tiny 0.1–0.2 grind moves. Target profile: chocolate/caramel/sweetness high, bitterness/acidity low, medium body, smooth finish.
- Favorite hot baseline (Altozano gold standard): 64 g coffee, Ode setting 5.4, 8 cups, 197°F, ratio 1:17, bloom on, bloom ratio 1:2, bloom time 30 sec, bloom temp 197°F, 4 batch pulses. Ode Gen 2 grind: hot brews live ~5.3–5.6; cold brew ~8.2 (coarser).
- Cold brew baseline: 80 g, Ode 8.2, 8 cups, room temp, ratio 1:14, 12 h, bloom on, bloom ratio 1:2, bloom time 45 sec, bloom temp 176°F. HARD LIMIT: in Cold Brew mode the Aiden only accepts bloom temp 176–210°F — never recommend a cold-brew bloom temp below 176°F.
- Adjustments: if bitter → coarsen 0.2–0.4, drop temp 1–2°F, shorten bloom; if flat → finer 0.1–0.2, +1°F, slightly longer bloom; if it needs sweetness → favor a finer grind before adding dose, then a slightly cooler brew + longer bloom, avoid over-extraction; if stomach upset → lower extraction, medium roast, washed coffees, avoid French Roast.
- Recipe formatting: present recipes in this exact order — ☕ Coffee, ⚙️ Grinder, 🥤 Cups, 🧾 Profile Name, ☕ Profile (or ❄️ Cold Brew Profile), 🌸 Bloom, 🫖 Batch Pulses, 📝 Notes. Always write the ratio as 1:17 / 1:16.5 / 1:14 — never as grams of water. Never use markdown tables. Never say "default" — always give actual values.

Recipes:
- Lean on his archive (search_recipes) before suggesting new ones.
- When evaluating "what can I make", consider both pantry contents and his appliances.
- When you CREATE a brand-new recipe in the conversation (one that isn't already in his archive) and write out its full ingredients and steps, call save_recipe with a suggested title so Josh gets a keep-or-discard card. Present the recipe in chat as usual AND call save_recipe — don't ask "want me to save this?" in text; the card handles that. Only do this for complete new recipes, not quick ideas, partial suggestions, or recipes that came from his archive.`;

export function buildSystemPrompt(store) {
  const dynamic = buildDynamicContext(store);
  return [
    {
      type: "text",
      text: FROZEN_SYSTEM,
      cache_control: { type: "ephemeral" },
    },
    {
      type: "text",
      text: dynamic,
    },
  ];
}

function buildDynamicContext(store) {
  const now = new Date();
  const pantryCount = (store.inventory || []).length;
  const recipeCount = (store.recipes || []).length;
  const beanCount = (store.beans || []).length;
  const applianceCount = (store.appliances || []).length;
  const tp = store.tasteProfile || {};
  const tasteLine = tp.sample_count
    ? `Coffee taste calibrated from ${tp.sample_count} brews (grind_bias=${tp.grind_bias}, temp_bias=${tp.temp_bias_c}°C, ratio_bias=${tp.ratio_bias}).`
    : `Coffee taste profile not yet calibrated.`;

  return `Current snapshot (call lookup tools for details):
- ${pantryCount} pantry items, ${recipeCount} recipes, ${beanCount} active coffee bags, ${applianceCount} appliances.
- ${tasteLine}
- Local time: ${now.toLocaleString()}.`;
}

// ---------------------------------------------------------------------------
// SSE streaming + tool use loop
// ---------------------------------------------------------------------------

async function* parseSSE(response) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const dataLines = chunk.split("\n").filter(l => l.startsWith("data:"));
      if (!dataLines.length) continue;
      const json = dataLines.map(l => l.slice(5).trimStart()).join("\n");
      try { yield JSON.parse(json); } catch {}
    }
  }
}

async function callOnce({ apiKey, system, messages, signal }) {
  const body = {
    model: MODEL,
    max_tokens: 4096,
    system,
    messages,
    tools: TOOL_DEFS,
    stream: true,
  };
  const res = await fetch(API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Claude API ${res.status}: ${err.slice(0, 300)}`);
  }
  return res;
}

/**
 * Run an agentic chat turn. Streams events to onEvent until the model
 * produces an end_turn (or a write tool is denied).
 *
 *   apiKey            Anthropic key
 *   messages          Array of {role, content} — full conversation so far
 *                     (will be mutated to include the assistant's reply +
 *                     any tool_results — caller observes via onEvent).
 *   store             useStore instance (for tool execution)
 *   requestApproval   async (toolName, input) => boolean
 *   onEvent           ({type, ...}) => void  — live updates
 *   signal            AbortSignal
 *
 * Event shapes emitted via onEvent:
 *   { type: 'assistant_start' }
 *   { type: 'text_delta', text }
 *   { type: 'tool_use_start', id, name, input_partial: '' }
 *   { type: 'tool_use_complete', id, name, input }
 *   { type: 'tool_result', id, name, content, is_error }
 *   { type: 'tool_denied', id, name }
 *   { type: 'turn_complete' }
 *   { type: 'error', error }
 */
export async function runChatTurn({ apiKey, messages, store, requestApproval, onEvent, signal }) {
  if (!apiKey) throw new Error("Add your Anthropic API key in Settings.");

  const system = buildSystemPrompt(store);

  // Outer loop: keep calling until end_turn (no tool_use)
  for (let iter = 0; iter < 8; iter++) {
    const res = await callOnce({ apiKey, system, messages, signal });

    onEvent({ type: "assistant_start" });

    const blocks = [];     // accumulated content blocks for the assistant turn
    let stopReason = null;
    let inputBuffers = {}; // index -> partial JSON string for tool_use blocks
    let usage = null;

    for await (const event of parseSSE(res)) {
      if (event.type === "message_start") {
        usage = event.message?.usage || null;
        continue;
      }

      if (event.type === "content_block_start") {
        const cb = event.content_block;
        blocks[event.index] = { ...cb };
        if (cb.type === "text") blocks[event.index].text = "";
        if (cb.type === "tool_use") {
          inputBuffers[event.index] = "";
          onEvent({ type: "tool_use_start", id: cb.id, name: cb.name });
        }
      }

      if (event.type === "content_block_delta") {
        const d = event.delta;
        const block = blocks[event.index];
        if (!block) continue;
        if (d.type === "text_delta") {
          block.text += d.text;
          onEvent({ type: "text_delta", text: d.text });
        } else if (d.type === "input_json_delta") {
          inputBuffers[event.index] += d.partial_json || "";
        }
      }

      if (event.type === "content_block_stop") {
        const block = blocks[event.index];
        if (block && block.type === "tool_use") {
          // Finalize input
          let input = {};
          try { input = inputBuffers[event.index] ? JSON.parse(inputBuffers[event.index]) : {}; } catch {}
          block.input = input;
          onEvent({ type: "tool_use_complete", id: block.id, name: block.name, input });
        }
      }

      if (event.type === "message_delta") {
        if (event.delta?.stop_reason) stopReason = event.delta.stop_reason;
        if (event.usage) usage = { ...(usage || {}), ...event.usage };
      }

      if (event.type === "message_stop") {
        // SSE done
      }
    }

    if (usage) emitUsage("chat", usage);

    // Append the assistant turn to history (preserve full content for the loop)
    const assistantContent = blocks.filter(Boolean);
    messages.push({ role: "assistant", content: assistantContent });

    if (stopReason !== "tool_use") {
      onEvent({ type: "turn_complete" });
      return;
    }

    // Execute any tool_use blocks
    const toolResults = [];
    for (const block of assistantContent) {
      if (block.type !== "tool_use") continue;
      const { id, name } = block;
      let input = block.input;

      // Confirm write tools. requestApproval resolves to `false` when denied,
      // or a truthy value when approved — an object payload (e.g. { title })
      // carries card edits (like a renamed recipe) that we merge into the input.
      if (WRITE_TOOL_NAMES.has(name)) {
        const approval = await requestApproval(name, input);
        if (!approval) {
          onEvent({ type: "tool_denied", id, name });
          toolResults.push({
            type: "tool_result",
            tool_use_id: id,
            content: `User declined to run ${name}. Ask what they'd like instead.`,
            is_error: false,
          });
          continue;
        }
        if (typeof approval === "object") input = { ...input, ...approval };
      }

      try {
        const result = await executeTool(name, input, store);
        onEvent({ type: "tool_result", id, name, content: result, is_error: false });
        toolResults.push({ type: "tool_result", tool_use_id: id, content: result });
      } catch (err) {
        const msg = err?.message || String(err);
        onEvent({ type: "tool_result", id, name, content: msg, is_error: true });
        toolResults.push({ type: "tool_result", tool_use_id: id, content: msg, is_error: true });
      }
    }

    messages.push({ role: "user", content: toolResults });
    // loop continues — model gets to react to tool results
  }

  onEvent({ type: "error", error: "Chat exceeded 8 tool-use iterations." });
}

// ---------------------------------------------------------------------------
// Persistence hook — multi-conversation
// ---------------------------------------------------------------------------
//
// Chats work like Claude/ChatGPT: many named conversations, one active at a
// time, all persisted in localStorage keyed by Clerk userId so they survive
// drawer close/open AND full page reloads.
//
// v2 store  (cucina-chats:<userId>):  { v:2, activeId, conversations: [conv] }
//   where conv = { id, title, createdAt, updatedAt, uiMessages, apiHistory, meter }
// v1 store  (cucina-chat:<userId>):   the old single-conversation blob; loaded
//   once and migrated into a single v2 conversation, then removed.
//
// The hook returns everything the drawer needs: the active conversation's
// uiMessages/apiHistoryRef/meter plus the conversation list and the
// newChat / selectChat / deleteChat / clear actions.

const CHATS_LS_PREFIX = "cucina-chats:"; // v2 multi-conversation store
const MAX_CONVERSATIONS = 40;            // cap the list; prune oldest by updatedAt

const EMPTY_METER = { tokens: 0, costCents: 0, calls: 0, cacheReadTokens: 0 };

function newConvId() {
  try { return crypto.randomUUID(); }
  catch { return "c" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }
}

function blankConversation() {
  const now = Date.now();
  return { id: newConvId(), title: "", createdAt: now, updatedAt: now, uiMessages: [], apiHistory: [], meter: { ...EMPTY_METER } };
}

// A conversation's title is derived from its first user message until the user
// (or a future rename) sets one explicitly.
function deriveTitle(uiMessages) {
  const first = uiMessages.find(m => m.role === "user" && m.text && m.text.trim());
  if (!first) return "";
  const t = first.text.trim().replace(/\s+/g, " ");
  return t.length > 42 ? t.slice(0, 42).trimEnd() + "…" : t;
}

// Coerce a persisted (or migrated) conversation into a well-formed object,
// trimming over-long transcripts the same way the single-conversation store did.
function normalizeConv(c) {
  if (!c || typeof c !== "object") return null;
  const uiMessages = Array.isArray(c.uiMessages) ? c.uiMessages.slice(-MAX_PERSISTED_MESSAGES) : [];
  const apiHistory = trimApiHistory(Array.isArray(c.apiHistory) ? c.apiHistory : []);
  return {
    id: c.id || newConvId(),
    title: typeof c.title === "string" ? c.title : "",
    createdAt: c.createdAt || Date.now(),
    updatedAt: c.updatedAt || c.createdAt || Date.now(),
    uiMessages,
    apiHistory,
    meter: c.meter && typeof c.meter === "object" ? { ...EMPTY_METER, ...c.meter } : { ...EMPTY_METER },
  };
}

// ---------------------------------------------------------------------------
// Remote sync (Supabase) — cross-device.
// ---------------------------------------------------------------------------
// localStorage stays the fast local cache / offline store. Supabase is the
// sync layer: on load we fetch remote rows and merge (newest updatedAt per id
// wins), then push anything the server is missing or stale on. All calls fail
// soft — if the table doesn't exist yet or the device is offline, chat keeps
// working from localStorage alone.

function rowToConv(r) {
  return normalizeConv({
    id: r.id,
    title: r.title,
    createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now(),
    updatedAt: r.updated_at ? new Date(r.updated_at).getTime() : Date.now(),
    uiMessages: r.ui_messages,
    apiHistory: r.api_history,
    meter: r.meter,
  });
}

async function fetchRemoteConversations() {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase
      .from("chat_conversations")
      .select("id,title,created_at,updated_at,ui_messages,api_history,meter")
      .order("updated_at", { ascending: false })
      .limit(MAX_CONVERSATIONS);
    if (error) { console.warn("chat sync: fetch failed —", error.message); return null; }
    return (data || []).map(rowToConv).filter(Boolean);
  } catch (e) {
    console.warn("chat sync: fetch threw —", e?.message || e);
    return null;
  }
}

async function upsertRemoteConversation(userId, conv) {
  // Never persist empty "New chat" placeholders remotely (matches local).
  if (!supabase || !userId || !conv || conv.uiMessages.length === 0) return;
  try {
    const row = {
      id: conv.id,
      user_id: userId,
      title: conv.title || "",
      ui_messages: conv.uiMessages.slice(-MAX_PERSISTED_MESSAGES),
      api_history: trimApiHistory(conv.apiHistory),
      meter: conv.meter,
      updated_at: new Date(conv.updatedAt || Date.now()).toISOString(),
      created_at: new Date(conv.createdAt || Date.now()).toISOString(),
    };
    const { error } = await supabase.from("chat_conversations").upsert(row, { onConflict: "id" });
    if (error) console.warn("chat sync: upsert failed —", error.message);
  } catch (e) {
    console.warn("chat sync: upsert threw —", e?.message || e);
  }
}

async function deleteRemoteConversation(id) {
  if (!supabase || !id) return;
  try {
    const { error } = await supabase.from("chat_conversations").delete().eq("id", id);
    if (error) console.warn("chat sync: delete failed —", error.message);
  } catch (e) {
    console.warn("chat sync: delete threw —", e?.message || e);
  }
}

// Union local + remote by id; newest updatedAt wins. Also returns the subset
// the server is missing or stale on, so the caller can push those up once.
function mergeConversations(localList, remoteList) {
  const remoteById = new Map(remoteList.map(c => [c.id, c]));
  const byId = new Map();
  for (const c of localList) byId.set(c.id, c);
  for (const c of remoteList) {
    const cur = byId.get(c.id);
    if (!cur || c.updatedAt >= cur.updatedAt) byId.set(c.id, c);
  }
  const merged = [...byId.values()]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_CONVERSATIONS);
  const toPush = merged.filter(c => {
    if (c.uiMessages.length === 0) return false;
    const r = remoteById.get(c.id);
    return !r || c.updatedAt > r.updatedAt;
  });
  return { merged, toPush };
}

function loadAll(userId) {
  if (!userId || typeof window === "undefined") return { conversations: [], activeId: null };

  // v2 — the multi-conversation store
  try {
    const raw = localStorage.getItem(CHATS_LS_PREFIX + userId);
    if (raw) {
      const parsed = JSON.parse(raw);
      const conversations = (Array.isArray(parsed.conversations) ? parsed.conversations : [])
        .map(normalizeConv).filter(Boolean);
      if (conversations.length) {
        const activeId = conversations.some(c => c.id === parsed.activeId) ? parsed.activeId : conversations[0].id;
        return { conversations, activeId };
      }
    }
  } catch { /* fall through to migration */ }

  // v1 → v2 migration — fold the old single blob into one conversation
  try {
    const legacy = localStorage.getItem(HISTORY_LS_PREFIX + userId);
    if (legacy) {
      const p = JSON.parse(legacy);
      const conv = normalizeConv({
        uiMessages: p.uiMessages,
        apiHistory: p.apiHistory,
        meter: p.meter,
        title: deriveTitle(Array.isArray(p.uiMessages) ? p.uiMessages : []),
      });
      if (conv && conv.uiMessages.length) return { conversations: [conv], activeId: conv.id };
    }
  } catch { /* ignore */ }

  return { conversations: [], activeId: null };
}

function trimApiHistory(arr) {
  if (arr.length <= MAX_PERSISTED_MESSAGES) return arr;
  // Trim from the front but ensure the trimmed history starts on a user
  // message — Claude rejects an assistant-led history.
  let trimmed = arr.slice(arr.length - MAX_PERSISTED_MESSAGES);
  while (trimmed.length && trimmed[0].role !== "user") trimmed.shift();
  return trimmed;
}

function saveAll(userId, conversations, activeId) {
  if (!userId || typeof window === "undefined") return;
  try {
    // Empty "New chat" placeholders are ephemeral — don't persist them. Keep
    // the most-recently-touched conversations up to the cap.
    const kept = conversations
      .filter(c => c.uiMessages.length > 0)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, MAX_CONVERSATIONS);
    const payload = {
      v: 2,
      activeId: kept.some(c => c.id === activeId) ? activeId : (kept[0]?.id ?? null),
      conversations: kept.map(c => ({
        id: c.id,
        title: c.title,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        uiMessages: c.uiMessages.slice(-MAX_PERSISTED_MESSAGES),
        apiHistory: trimApiHistory(c.apiHistory),
        meter: c.meter,
      })),
    };
    localStorage.setItem(CHATS_LS_PREFIX + userId, JSON.stringify(payload));
    // Drop the legacy single-conversation key once we've migrated off it.
    try { localStorage.removeItem(HISTORY_LS_PREFIX + userId); } catch { /* ignore */ }
  } catch {
    // quota exceeded or serialization failure — silently drop
  }
}

// Sonnet 4.6 pricing — must match useStore.js cost calc so the in-chat
// meter agrees with the Daily AI usage tile.
//   $3 input / $15 output / $0.30 cache-read / $3.75 cache-create per 1M
function usageToCostCents(u) {
  if (!u) return 0;
  const inp = +(u.input_tokens || 0);
  const out = +(u.output_tokens || 0);
  const cr = +(u.cache_read_input_tokens || 0);
  const cc = +(u.cache_creation_input_tokens || 0);
  return (inp * 0.0003) + (out * 0.0015) + (cr * 0.00003) + (cc * 0.000375);
}

export function useChatHistory(userId) {
  // `conversations` holds full conversation objects; the drawer's list only
  // reads id/title/updatedAt, while the transcript reads the active one's
  // uiMessages/meter.
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // apiHistoryRef always points at the ACTIVE conversation's apiHistory array.
  // runChatTurn mutates it in place (messages.push), and because it's the same
  // array reference stored on the active conversation, those mutations persist
  // with the next setUiMessages flush. Repoint it on every switch/create.
  const apiHistoryRef = useRef([]);
  // Mirrors of state for use inside stable callbacks without stale closures.
  const activeIdRef = useRef(null);
  const conversationsRef = useRef([]);
  const userIdRef = useRef(null);
  const hydratedFor = useRef(null);

  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // Hydrate when userId becomes available (or changes). Guarantee an active
  // conversation exists so the composer always has somewhere to write. We
  // hydrate from localStorage first (instant), then pull remote and merge so
  // conversations started on another device show up.
  useEffect(() => {
    if (!userId || hydratedFor.current === userId) return;
    let { conversations: cs, activeId: aid } = loadAll(userId);
    if (cs.length === 0) {
      const c = blankConversation();
      cs = [c];
      aid = c.id;
    }
    const active = cs.find(c => c.id === aid) || cs[0];
    setConversations(cs);
    setActiveId(active.id);
    apiHistoryRef.current = active.apiHistory;
    activeIdRef.current = active.id;
    conversationsRef.current = cs;
    hydratedFor.current = userId;

    // Remote merge — fire-and-forget; local already works if this fails.
    let cancelled = false;
    (async () => {
      const remote = await fetchRemoteConversations();
      if (cancelled || remote == null || userIdRef.current !== userId) return;
      const { merged, toPush } = mergeConversations(conversationsRef.current, remote);
      // Keep the active conversation if it survived; otherwise fall back to the
      // newest. Repoint apiHistoryRef in case merge replaced the active object
      // (safe: no turn can be in flight during initial hydrate).
      const keepId = merged.some(c => c.id === activeIdRef.current)
        ? activeIdRef.current
        : (merged[0]?.id ?? activeIdRef.current);
      const keep = merged.find(c => c.id === keepId);
      if (keep) apiHistoryRef.current = keep.apiHistory;
      setConversations(merged);
      setActiveId(keepId);
      // Push anything the server is missing/stale on.
      toPush.forEach(c => upsertRemoteConversation(userId, c));
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Debounced push of the ACTIVE conversation to Supabase. Streaming mutates it
  // many times; the cleanup resets the timer so we only upsert ~1.5s after the
  // last change, not on every token.
  useEffect(() => {
    if (!userId || hydratedFor.current !== userId) return;
    const conv = conversations.find(c => c.id === activeId);
    if (!conv || conv.uiMessages.length === 0) return;
    const t = setTimeout(() => { upsertRemoteConversation(userId, conv); }, 1500);
    return () => clearTimeout(t);
  }, [userId, conversations, activeId]);

  // Persist on any meaningful change.
  useEffect(() => {
    if (!userId || hydratedFor.current !== userId) return;
    saveAll(userId, conversations, activeId);
  }, [userId, conversations, activeId]);

  // Subscribe to chat-tagged usage events and fold them into the ACTIVE
  // conversation's meter (a turn only ever runs for the active conversation).
  useEffect(() => {
    if (!userId) return;
    const handler = (e) => {
      const detail = e.detail || {};
      if (detail.kind !== "chat" || !detail.usage) return;
      const u = detail.usage;
      const tokens = +(u.input_tokens || 0) + +(u.output_tokens || 0);
      const cacheReadTokens = +(u.cache_read_input_tokens || 0);
      const costCents = usageToCostCents(u);
      const id = activeIdRef.current;
      setConversations(prev => prev.map(c => c.id !== id ? c : {
        ...c,
        meter: {
          tokens: c.meter.tokens + tokens,
          costCents: c.meter.costCents + costCents,
          calls: c.meter.calls + 1,
          cacheReadTokens: c.meter.cacheReadTokens + cacheReadTokens,
        },
      }));
    };
    window.addEventListener("cucina-ai-usage", handler);
    return () => window.removeEventListener("cucina-ai-usage", handler);
  }, [userId]);

  // Update the active conversation's transcript. Re-captures apiHistoryRef
  // (same array reference) so pushes made during the turn are persisted, and
  // auto-titles from the first user message.
  const setUiMessages = useCallback((updater) => {
    const id = activeIdRef.current;
    if (!id) return;
    setConversations(prev => prev.map(c => {
      if (c.id !== id) return c;
      const nextUi = typeof updater === "function" ? updater(c.uiMessages) : updater;
      return {
        ...c,
        uiMessages: nextUi,
        apiHistory: apiHistoryRef.current,
        updatedAt: Date.now(),
        title: c.title || deriveTitle(nextUi),
      };
    }));
  }, []);

  const selectChat = useCallback((id) => {
    const conv = conversationsRef.current.find(c => c.id === id);
    if (!conv) return;
    apiHistoryRef.current = conv.apiHistory;
    activeIdRef.current = id;
    setActiveId(id);
  }, []);

  // Start a new chat — but reuse an existing empty one rather than piling up
  // blank "New chat" placeholders (matches ChatGPT/Claude behavior).
  const newChat = useCallback(() => {
    const existingEmpty = conversationsRef.current.find(c => c.uiMessages.length === 0);
    if (existingEmpty) {
      apiHistoryRef.current = existingEmpty.apiHistory;
      activeIdRef.current = existingEmpty.id;
      setActiveId(existingEmpty.id);
      return;
    }
    const c = blankConversation();
    apiHistoryRef.current = c.apiHistory;
    activeIdRef.current = c.id;
    setConversations(prev => [c, ...prev]);
    setActiveId(c.id);
  }, []);

  const deleteChat = useCallback((id) => {
    const remaining = conversationsRef.current.filter(c => c.id !== id);
    if (activeIdRef.current === id) {
      const next = remaining[0] || blankConversation();
      apiHistoryRef.current = next.apiHistory;
      activeIdRef.current = next.id;
      setConversations(remaining.length ? remaining : [next]);
      setActiveId(next.id);
    } else {
      setConversations(remaining);
    }
    deleteRemoteConversation(id);
  }, []);

  // Give a conversation an explicit title (falls back to auto-derive if
  // cleared). Upserts that one conversation directly so a rename of a
  // non-active chat still syncs.
  const renameChat = useCallback((id, title) => {
    const clean = (title || "").trim().slice(0, 80);
    const conv = conversationsRef.current.find(c => c.id === id);
    const updated = conv ? { ...conv, title: clean, updatedAt: Date.now() } : null;
    setConversations(prev => prev.map(c => c.id !== id ? c : { ...c, title: clean, updatedAt: Date.now() }));
    if (updated && userIdRef.current) upsertRemoteConversation(userIdRef.current, updated);
  }, []);

  // Reset the active conversation in place (used by the header's clear button).
  // The row becomes empty, so drop it from the server too.
  const clear = useCallback(() => {
    const id = activeIdRef.current;
    apiHistoryRef.current = [];
    const fresh = apiHistoryRef.current;
    setConversations(prev => prev.map(c => c.id !== id ? c : {
      ...c, uiMessages: [], apiHistory: fresh, title: "", meter: { ...EMPTY_METER }, updatedAt: Date.now(),
    }));
    deleteRemoteConversation(id);
  }, []);

  const active = conversations.find(c => c.id === activeId) || null;

  return {
    conversations,
    activeId,
    uiMessages: active ? active.uiMessages : [],
    setUiMessages,
    apiHistoryRef,
    meter: active ? active.meter : EMPTY_METER,
    newChat,
    selectChat,
    deleteChat,
    renameChat,
    clear,
  };
}

// Format a cents value tightly: <1¢, 7¢, 23¢, $0.85, $1.42
export function formatCostCents(cents) {
  if (cents == null) return "—";
  if (cents < 0.5) return "<1¢";
  if (cents < 100) return `${Math.round(cents)}¢`;
  return `$${(cents / 100).toFixed(2)}`;
}

export function formatTokens(n) {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n / 1000)}k`;
}
